import {useCallback, useEffect, useState} from 'react'
import axios from 'axios'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTCardBody, KTIcon} from '../../../_metronic/helpers'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'
import {useCan} from '../../modules/auth/core/AdminPermissions'
import {InviteAdminModal} from './InviteAdminModal'
import {EditAdminModal, AdminRow, InviteRow} from './EditAdminModal'

const AdminUsersWrapper = () => {
  const canInvite = useCan('admin_users.create')
  const canEdit = useCan('admin_users.edit')
  const [loading, setLoading] = useState(true)
  const [admins, setAdmins] = useState<AdminRow[]>([])
  const [invites, setInvites] = useState<InviteRow[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editAdmin, setEditAdmin] = useState<AdminRow | null>(null)
  const [editInvite, setEditInvite] = useState<InviteRow | null>(null)

  const load = useCallback(async () => {
    const token = AdminAuth()
    if (!token) return
    setLoading(true)
    try {
      const res = await axios.get(`${apiUrl}/admin/users`, {
        headers: {'x-access-token': token},
        params: {page, search: search.trim() || undefined},
      })
      const d = res.data?.data
      setAdmins(Array.isArray(d?.admins) ? d.admins : [])
      setInvites(Array.isArray(d?.invites) ? d.invites : [])
      setTotal(typeof d?.pager?.total === 'number' ? d.pager.total : 0)
    } catch {
      setAdmins([])
      setInvites([])
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    void load()
  }, [load])

  const limit = 20
  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <>
      <PageTitle breadcrumbs={[]}>Admin users</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative my-1'>
              <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-5' />
              <input
                type='text'
                className='form-control form-control-solid w-250px ps-13'
                placeholder='Search name or email'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1)
                    void load()
                  }
                }}
              />
            </div>
          </div>
          <div className='card-toolbar'>
            {canInvite ? (
              <button type='button' className='btn btn-primary' onClick={() => setInviteOpen(true)}>
                Invite admin
              </button>
            ) : null}
          </div>
        </div>
        <KTCardBody className='py-4'>
          {loading ? (
            <div className='text-center py-10'>Loading…</div>
          ) : (
            <>
              <h4 className='mb-4'>Active admins</h4>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>2FA</th>
                      <th>Status</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((a) => (
                      <tr key={a.id}>
                        <td>{a.name || '—'}</td>
                        <td>{a.email}</td>
                        <td>
                          <span className='badge badge-light-primary'>
                            {a.assignedRole?.name || a.role || '—'}
                          </span>
                        </td>
                        <td>{a.totp_enabled ? 'On' : 'Off'}</td>
                        <td>{a.is_active === false ? 'Inactive' : 'Active'}</td>
                        <td className='text-end'>
                          {canEdit ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => setEditAdmin(a)}
                            >
                              Manage
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h4 className='mt-10 mb-4'>Pending invites</h4>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Expires</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email}</td>
                        <td>{inv.name || '—'}</td>
                        <td>
                          <span className='badge badge-light'>{inv.role?.name || inv.role_id}</span>
                        </td>
                        <td>{new Date(inv.expires_at).toLocaleString()}</td>
                        <td className='text-end'>
                          {canInvite ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => setEditInvite(inv)}
                            >
                              Manage
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='d-flex justify-content-between align-items-center mt-5'>
                <span className='text-muted fs-7'>
                  Page {page} of {totalPages}
                </span>
                <div className='btn-group'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </KTCardBody>
      </div>

      <InviteAdminModal show={inviteOpen} onHide={() => setInviteOpen(false)} onCreated={load} />
      <EditAdminModal
        show={!!editAdmin || !!editInvite}
        onHide={() => {
          setEditAdmin(null)
          setEditInvite(null)
        }}
        onDone={load}
        mode={editInvite ? 'invite' : 'admin'}
        admin={editAdmin}
        invite={editInvite}
      />
    </>
  )
}

export default AdminUsersWrapper
